import React, { useEffect, useState, useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import LoadingButton from '@mui/lab/LoadingButton';
import SearchIcon from '@material-ui/icons/Search';
import Paper from '@material-ui/core/Paper';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import ItemComboBox, { ComboBoxItemType } from '../../../atoms/ComboBox/ItemComboBox';
import ItemComboBoxStr, { ComboBoxItemTypeS } from '../../../atoms/ComboBox/ItemComboBoxStr';
import MessageDialog, { MessageDialogResponse } from '../../../atoms/Dialog/MessageDialog';
import ConfirmDialog, { ConfirmDialogResponse } from '../../../atoms/Dialog/ConfirmDialog';
import DateInput from '../../../atoms/Calendar/DateInput';
import { ErrorCode } from '../../../../ErrorCode';

import { TableBaseStyle } from './../PartialDischargeStyle';
import {
    SelectDataType
    , GetPartialDischargeGraphDevListData
    , GetLatesTime
    , GetDataTimeList
    , GetPartialDischargeGraphItemData
    , PartialDischargeGraphXYDataModel
    , upDateSelectTimeList
} from './../PartialDischarge';
import PartialDischargeGraphLeftChart from './PartialDischargeGraphLeftChart';
import PartialDischargeGraphRightChart from './PartialDischargeGraphRightChart';
import { useModal } from '../../../../function/useModal';

type PartialDischargeGraphPageProps = {
};


const PartialDischargeGraphPage: React.FC<PartialDischargeGraphPageProps> = (props) => {
    const tableStyle = TableBaseStyle();
    const { t } = useTranslation();

    const [nameDevSetComboBoxItemList, setNameDevSetComboBoxItemList] = useState<ComboBoxItemType[]>([]);//デバイスコンボボックス
    const [selectTimeComboBoxList, setSelectTimeComboBoxList] = useState<ComboBoxItemTypeS[]>([]); //時間コンボボックス
    const [selectViewTimeComboBoxList, setSelectViewTimeComboBoxList] = useState<ComboBoxItemType[]>([]); //表示時間コンボボックス
    const [phaseCharacteristicsGraphList, setPhaseCharacteristicsGraphList] = useState<PartialDischargeGraphXYDataModel[]>();//位相特性グラフアイテム
    const [cumulativeFrequencyGraphList, setCumulativeFrequencyGraphList] = useState<PartialDischargeGraphXYDataModel[]>();//累積頻度グラフアイテム
    const [measureValue, setMeasureValue] = useState<number>();//右側グラフのpd強度
    const [loading, setLoading] = React.useState(false);
    const [selectData, setSelectData] = useState<SelectDataType>({
        devIndex: 1,
        selectDate: dayjs(new Date()).format('YYYY-MM-DD'),
        selectTime: '',
        viwTime: 1
    });
    //const [buttonEnable, setButtonEnable] = useState<boolean>(true);//表示ボタン
    const [graphsEnable, setGraphsEnable] = useState<boolean>(false);//グラフアイテムの表示・非表示

    const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
    const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();
    const [dlgmsg, setDlgmsg] = useState<string>('');

    const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;

    /**
     * 初期処理
     */
    useEffect(() => {
        updateData();

    }, []);

    /**
     * 画面表示項目更新
     * @param target 
     */
    const onChangeSelectData = (target: SelectDataType) => {
        setSelectData({ ...target });
    };

    /**
     * 画面初期表示処理
     */
    const updateData = () => {
        let nameDevSetList: ComboBoxItemType[] = [];
        let selectDateList: ComboBoxItemTypeS[] = [];
        let errorMessage: string = "";
        setLoading(true);
        setGraphsEnable(false);

        //表示時間コンボボックスデータ作成
        let viwTimeList: ComboBoxItemType[] = [];
        viwTimeList.push({ index: 10, name: t('PartialDischargeGraphPage.0_10秒', '0～10秒') });
        viwTimeList.push({ index: 5, name: t('PartialDischargeGraphPage.0_5秒', '0～5秒') });
        viwTimeList.push({ index: 1, name: t('PartialDischargeGraphPage.0_1秒', '0～1秒') });


        //機器情報取得
        GetPartialDischargeGraphDevListData()
            .then((res) => {
                console.debug(['--PartialDischargeCollectionModel--', res]);
                if (res.ResponceData.ErrorCode === ErrorCode.ERR_CODE_NONE) {
                    //機器番号取得確認
                    if (res.NameDevSetLists.length === 0) {
                        errorMessage = t('PartialDischargeGraphPage.機器番号のデータがありませんでした', '機器番号のデータがありませんでした');
                        throw new Error;
                    }
                    else {
                        nameDevSetList = (
                            res.NameDevSetLists.map<ComboBoxItemType>((ci) => {
                                return { index: ci.Key, name: ci.Disp };
                            })
                        );
                    }
                }
                //最新ファイルの時間取得
                return GetLatesTime(nameDevSetList[0].index);
            }).then((res) => {
                console.debug(['useEffect -- GetLatesTime ret', res]);
                if (res.ResponceData.ResponseCode === ErrorCode.ERR_CODE_SYSTEM_ERROR) {
                    errorMessage = t('PartialDischargeGraphPage.対象の最新時刻の取得に失敗しました', '対象の最新時刻の取得に失敗しました');
                    throw new Error;
                }

                //時間コンボボックス更新
                if (res.DateTimeList.length === 0) {
                    throw 'DateTimeListLengthError';;

                }
                res.DateTimeList.forEach((r) => {
                    selectDateList.push(upDateSelectTimeList(r));
                })
                selectData.devIndex = nameDevSetList[0].index;
                if (selectDateList.length !== 0) {
                    selectData.selectDate = dayjs(new Date(res.DateTimeList[0])).format('YYYY-MM-DD');
                    selectData.selectTime = selectDateList[0].key;
                }
                else {
                    selectData.selectDate = dayjs(new Date()).format('YYYY-MM-DD');
                    selectData.selectTime = "";
                }

                selectData.viwTime = 1;
                setSelectData(selectData);
                setNameDevSetComboBoxItemList(nameDevSetList);
                setSelectTimeComboBoxList(selectDateList);
                setSelectViewTimeComboBoxList([...viwTimeList]);

                //各グラフのアイテム取得
                return GetPartialDischargeGraphItemData(
                    selectData.devIndex
                    , selectData.selectDate + " " + selectData.selectTime
                    , selectData.viwTime
                );
            }).then((res) => {
                console.debug(['useEffect -- GetPartialDischargeGraphItemData', res]);
                if (res === null) {
                    errorMessage = t('PartialDischargeGraphPage.グラフの取得に失敗しました', 'グラフの取得に失敗しました');
                    throw new Error;
                }
                console.debug(['setSelectData -selectData', selectData]);

                setPhaseCharacteristicsGraphList(res.PhaseCharacteristicsGraphList);
                setCumulativeFrequencyGraphList(res.CumulativeFrequencyGraphList);
                setMeasureValue(res.MeasureValue);
                setGraphsEnable(true);
            }).catch((e) => {
                if (e === 'DateTimeListLengthError') {
                    setSelectViewTimeComboBoxList([...viwTimeList]);
                    setNameDevSetComboBoxItemList(nameDevSetList);
                    console.debug(["計測日の取得に失敗しました", e, selectData])
                }
                else {
                    setDlgmsg(errorMessage === "" ? t('PartialDischargeGraphPage.データ取得中にエラーが発生しました', 'データ取得中にエラーが発生しました') : errorMessage);
                    actMsgDlg.open();
                    console.debug(["e", e]);
                    //setButtonEnable(true);
                    setGraphsEnable(false);
                }

            }).finally(() => {
                //setButtonEnable(true);
                setLoading(false);

                console.debug(["update end"]);
            });
    };

    /**
    * 機器選択変更Event
    * @param value
    */
    const onDevIndexSelectDataChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const selectDevIndex = Number(e.target.value);
        let selectDateList: ComboBoxItemTypeS[] = [];
        //計測時間再取得処理
        GetLatesTime(selectDevIndex).then((res) => {
            res.DateTimeList.forEach((r) => {
                selectDateList.push(upDateSelectTimeList(r));
            });
            console.debug(["onDevIndexSelectDataChange -selectDateList-", selectDateList]);

            if (selectDateList.length !== 0) {
                selectData.selectDate = dayjs(new Date(res.DateTimeList[0])).format('YYYY-MM-DD');
                selectData.selectTime = selectDateList[0].key;
                //setButtonEnable(false);
            }
            else {
                selectData.selectDate = dayjs(new Date()).format('YYYY-MM-DD');
                selectData.selectTime = "";
                //setButtonEnable(true);
            }
            selectData.devIndex = selectDevIndex;
            console.debug(["onDevIndexSelectDataChange -selectDevIndex-", selectDevIndex, selectData]);

            setSelectTimeComboBoxList(selectDateList);
            onChangeSelectData(selectData);
        });
    };
    /**
     * 計測日(年月日)変更
     * @param value
     */
    const changeCalendarValueAction = (value: string) => {
        let selectDateList: ComboBoxItemTypeS[] = [];

        //計測時間再取得処理
        GetDataTimeList(selectData.devIndex, value).then((res) => {
            res.DateTimeList.forEach((r) => {
                selectDateList.push(upDateSelectTimeList(r));
            })
            console.debug(["changeCalendarValueAction -upDateSelectTimeList", selectDateList]);

            if (selectDateList.length !== 0) {
                selectData.selectTime = selectDateList[0].key;
                //setButtonEnable(false);
            }
            else {
                selectData.selectTime = "";
                //setButtonEnable(true);
            }
            selectData.selectDate = value;
            console.debug(["changeCalendarValueAction -selectData", selectData]);

            setSelectTimeComboBoxList(selectDateList);
            onChangeSelectData(selectData);
        });
    };
    /**
    * 計測日(時間)変更
    * @param value
    */
    const changeTimeValueAction = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const selectTimeIndex = String(e.target.value);
        setSelectData((prev) => ({ ...prev, selectTime: selectTimeIndex }));
        //setButtonEnable(false);
    };
    /**
     * 表示時間変更
     * @param e 
     */
    const changeViewTimeSelectAction = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const viwTimeNo = Number(e.target.value);

        selectData.viwTime = viwTimeNo;
        console.debug(["viwTimeNo", viwTimeNo, selectData]);
        onChangeSelectData(selectData);
        // if (selectData.selectTime !== "") {
        //     setButtonEnable(false);
        // }

    };

    /**
     * 表示ボタンクリック
     */
    const handleDispButtonClick = () => {
        setLoading(true);
        graphDataChange(selectData);
        //setButtonEnable(true);
    };
    /**
     * グラフの表示変更
     * @param value 
     */
    const graphDataChange = (value: SelectDataType) => {
        //グラフ再読み込み
        GetPartialDischargeGraphItemData(
            selectData.devIndex
            , selectData.selectDate + " " + selectData.selectTime
            , selectData.viwTime
        ).then((res) => {
            console.debug(["handleSelectDataChange", res]);
            setPhaseCharacteristicsGraphList(res.PhaseCharacteristicsGraphList);
            setCumulativeFrequencyGraphList(res.CumulativeFrequencyGraphList);
            setMeasureValue(res.MeasureValue);
            setLoading(false);
            setGraphsEnable(true);
        }).catch(e => {
            setDlgmsg(t('PartialDischargeGraphPage.データ取得中にエラーが発生しました', 'データ取得中にエラーが発生しました'));
            actMsgDlg.open();
            console.debug(["e", e]);
            //setButtonEnable(false);
        }).finally(() => {
            setLoading(false);

            console.debug(["update end"]);
        });

    };
    /**
     * 位相特性グラフの呼び出し
     */
    const leftChartControlMemo = useMemo(() => {
        return (
            <td style={{ backgroundColor: 'white', width: '90%', height: '90%' }}>
                <PartialDischargeGraphLeftChart
                    phaseCharacteristicsGraphList={phaseCharacteristicsGraphList}
                />
            </td>
        );
    }, [phaseCharacteristicsGraphList]);
    /**
     * 累積頻度グラフの呼び出し
     */
    const rightChartControlMemo = useMemo(() => {
        return (
            <PartialDischargeGraphRightChart
                cumulativeFrequencyGraphList={cumulativeFrequencyGraphList}
            />
        );
    }, [cumulativeFrequencyGraphList])

    return (
        <>
            <Grid container justify="flex-start" spacing={1} direction="row" alignItems="center" style={{ margin: 2 }}>
                <Grid item xs={8}>
                    <Grid container justify="flex-start" spacing={1} direction="row" alignItems="center" style={{ margin: 2 }}>
                        {/**機器番号・名称 */}
                        <div style={{ width: 20 }}></div>
                        <ItemComboBox
                            key={"devComb"}
                            label={t('PartialDischargeGraphPage.機器', '機器：')}
                            itemList={nameDevSetComboBoxItemList}
                            value={selectData.devIndex}
                            labelStyle={tableStyle.tableCell}
                            itemBoxStyle={tableStyle.itemBoxStyle}
                            onChangeEvent={onDevIndexSelectDataChange}

                        />
                        {/** 計測日 年月日 */}
                        <div style={{ width: 20 }}></div>
                        <TableCell className={tableStyle.tableCell}>
                            <DateInput
                                labelCssString={tableStyle.tableCell}
                                label={t('PartialDischargeGraphPage.計測日', '計測日：')}
                                initialValue={dayjs(selectData.selectDate).format('YYYY-MM-DD')}
                                labelPosition={1}
                                onDateChange={changeCalendarValueAction}

                            ></DateInput>
                        </TableCell>
                        {/** 計測日 時間*/}
                        <div style={{ width: 20 }}></div>
                        <ItemComboBoxStr
                            itemList={selectTimeComboBoxList}
                            label={''}
                            value={selectData.selectTime}
                            onChangeEvent={changeTimeValueAction}
                        ></ItemComboBoxStr>
                        {/** 表示時間*/}
                        <div style={{ width: 20 }}></div>
                        <ItemComboBox
                            key={"viewTimeComb"}
                            label={t('PartialDischargeGraphPage.表示時間', '表示時間：')}
                            itemList={selectViewTimeComboBoxList}
                            value={selectData.viwTime}
                            labelStyle={tableStyle.tableCell}
                            itemBoxStyle={tableStyle.itemBoxStyle}
                            onChangeEvent={changeViewTimeSelectAction}
                        />
                        {/** 表示*/}
                        <div style={{ width: 40 }}></div>
                        <LoadingButton
                            className={tableStyle.button}
                            startIcon={<SearchIcon />}
                            style={{ width: '100px' }}
                            loading={loading}
                            loadingPosition="start"
                            variant="contained"
                            color="primary"
                            //disabled={buttonEnable}
                            onClick={handleDispButtonClick}
                        >
                            {t('PartialDischargeGraphPage.表示', '表示')}
                        </LoadingButton>
                    </Grid>
                </Grid>
                {/** 位相特性グラフ */}
                <Grid item xs={6}>
                    <Paper elevation={3} style={{ width: "100%", height: 'calc(100vh - 310px)' }} >
                        {graphsEnable === true ?
                            <table style={{ width: '100%', height: '100%', tableLayout: 'fixed' }} >
                                <tr>
                                    <td
                                        style={{ backgroundColor: 'white', width: '80px', height: '100%' }}
                                        rowSpan={3}
                                        align='center'
                                    >
                                        {t('PartialDischargeGraphPage.PD強度', 'PD強度[V]')}
                                    </td>
                                    <td
                                        style={{ backgroundColor: 'white', width: '90%', height: '20px' }}
                                        align='center'
                                    >
                                        {t('PartialDischargeGraphPage.位相特性', '位相特性')}
                                    </td>
                                </tr>
                                <tr>
                                    {leftChartControlMemo}
                                </tr>
                                <tr>
                                    <td
                                        style={{ backgroundColor: 'white', width: '90%', height: '20px' }}
                                        align='center'
                                    >
                                        {t('PartialDischargeGraphPage.位相', '位相[°]')}
                                    </td>
                                </tr>
                            </table>
                            : null
                        }
                    </Paper>
                </Grid>
                {/** 累積頻度グラフ */}
                <Grid item xs={6}>
                    <Paper elevation={3} style={{ width: "100%", height: 'calc(100vh - 310px)' }} >
                        {graphsEnable === true ?
                            <table style={{ width: '100%', height: '100%', tableLayout: 'fixed' }} >
                                <tr>
                                    <td
                                        style={{ backgroundColor: 'white', width: '80px', height: '100%' }}
                                        rowSpan={3}
                                        align='center'
                                    >
                                        {t('PartialDischargeGraphPage.発生頻度', '発生頻度[個]')}
                                    </td>
                                    <td
                                        style={{ backgroundColor: 'white', width: '80%', height: '20px' }}
                                        align='center'
                                    >
                                        {t('PartialDischargeGraphPage.累積頻度', '累積頻度')}
                                    </td>
                                    <td
                                        style={{ backgroundColor: 'white', width: '80px', height: '100%' }}
                                        rowSpan={3}
                                        align='center'
                                    >
                                        60pps {measureValue}[v]
                                    </td>
                                </tr>
                                <tr>
                                    {rightChartControlMemo}
                                </tr>
                                <tr>
                                    <td
                                        style={{ backgroundColor: 'white', width: '90%', height: '20px' }}
                                        align='center'
                                    >
                                        {t('PartialDischargeGraphPage.PD強度', 'PD強度[V]')}
                                    </td>
                                </tr>
                            </table>
                            : null
                        }

                    </Paper>
                </Grid>
            </Grid>
            {msgdlg}
        </>
    )
}
export default PartialDischargeGraphPage;